<script setup>
import { computed, ref } from "vue";
import { useStore } from "@/js/store";
import ReasonToSkipLead from "@/js/components/Campaign/CampaignSettings/ReasonToSkipLead.vue";
import { deepClone } from "@/js/helpers/utils";

const props = defineProps({
	value: {
		type: Array,
		required: true,
	},
	haveToUseDefault: {
		type: Boolean,
		required: false,
		default: false,
	}
});
const emit = defineEmits(["input"]);

const store = useStore();
const basicReasons = deepClone(store.getters.basicReasons);
const advancedReasons = ref(deepClone(store.getters.advancedReasons));
const reasonsForSkip = [...basicReasons, ...advancedReasons.value];

if (!props.haveToUseDefault) {
	setInitialValuesFromProps();
} else {
	changeSelections();
}

function setInitialValuesFromProps() {
	reasonsForSkip.forEach((reason) => {
		reason.skip = props.value.includes(reason.value);
	});
}
const slowOptionText = `These ${advancedReasons.value.length} options will cause slower lead upload time if selected`;
const advancedReasonSelected = computed(() => {
	return advancedReasons.value.some((reason) => reason.skip);
});

function changeSelections() {
	emit("input", reasonsForSkip.filter((reason) => reason.skip).map((reason) => reason.value));
}
</script>

<template>
	<section class="skip-reasons">
		<p class="body_M">
			<slot>Do not add a lead to the campaign if:</slot>
		</p>
		<br>
		<div class="skip-reasons__checks">
			<ReasonToSkipLead
				v-for="reason in basicReasons"
				:key="reason.value"
				:value="reason"
				@change="changeSelections"
			/>
		</div>
		<div
			class="skip-reasons__checks skip-reasons__checks--adv"
			:class="{ 'skip-reasons__checks--selected': advancedReasonSelected }"
			:aria-label="slowOptionText"
		>
			<ReasonToSkipLead
				v-for="reason in advancedReasons"
				:key="reason.value"
				:value="reason"
				aria-describedby="hint"
				@change="changeSelections"
			/>

			<p
				id="hint"
				class="skip-reasons__disclamer body_S"
				:class="{ 'skip-reasons__disclamer--hidden': !advancedReasonSelected }"
			>
				<Icon icon="filter-long" />
				{{ slowOptionText }}
			</p>
		</div>
	</section>
</template>

<style lang="sass">
.skip-reasons
	--offset: 12px
	&__
		&checks
			margin-left: calc(var(--offset) * -1)
			padding-left: var(--offset)
			&--
				&adv
					border-radius: var(--bdrs)
					border: 1px solid transparent
					transition: padding var(--transition), border-color var(--transition), background-color var(--transition)
				&selected
					padding: var(--offset)
					border-color: var(--neutral-200)
					background-color: var(--neutral-50)
		&disclamer
			transition: opacity var(--transition)
			display: flex
			gap: 4px
			&--
				&hidden
					opacity: 0
					height: 0
					pointer-events: none
</style>
